import {
    type AssetClientContext,
    type AssetDelivery,
    type AssetPage,
    assetKeys,
    deliveryManifest,
    type FolderList,
    folderKeys,
    getAsset,
    listFolders,
    searchAssets,
} from "@pej/asset-client";
import type { QueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import type {
    AssetFolderSummary,
    AssetLoader,
    AssetQuery,
    AssetSearchPage,
    AssetType,
    FolderLoader,
} from "@/components/assets";
import {
    pickArtworkUrl,
    pickFontUrl,
    pickPreviewUrl,
    toAssetSummary,
    toFolderSummary,
    toSearchPage,
    toSearchRequest,
} from "./asset-projections";

/**
 * Reads for the artwork surfaces.
 *
 * Two shapes, one cache. The picker takes imperative `Promise`-returning
 * loaders because it owns its own search and paging state; the field reads
 * declaratively with a hook. Both route through the same `QueryClient` using
 * the key factories `@pej/asset-client` exports, so a rename in the picker is
 * visible in the field without a second round trip. Two caches for the same
 * data is how a just-renamed asset shows its old name one panel over.
 */

/**
 * Enough to fill the visible grid (eight above the fold) plus a screenful of
 * scroll, without making the first paint wait on a manifest request per item.
 */
const PAGE_SIZE = 24;

/**
 * Manifests are immutable for an asset generation — the service serves them
 * with a one-year cache — so there is no reason to re-ask within a session.
 */
const MANIFEST_STALE_TIME = Number.POSITIVE_INFINITY;

const EMPTY_PAGE: AssetSearchPage = { items: [], totalCount: 0, nextPageToken: null };

/**
 * Runs a read and converts a failure into a fallback value.
 *
 * Deliberately wraps `fetchQuery` rather than living inside a `queryFn`. The
 * app's `QueryCache.onError` is what redirects to SSO when a request fails with
 * a network error, and swallowing the failure inside the query function would
 * mean the query never errors, so that redirect never fires — an expired
 * session would look like an empty asset library. Letting the query fail and
 * catching here preserves both: the interceptor sees the error, and the picker
 * gets the non-rejecting loader its contract requires.
 */
async function withFallback<T>(fallback: T, run: () => Promise<T>): Promise<T> {
    try {
        return await run();
    } catch {
        return fallback;
    }
}

/**
 * Options for one asset's delivery manifest.
 *
 * Tolerates a null ctx/assetId because `enabled` does NOT gate this. Everything
 * passed to `useQuery` — the key above all — is built on every render; `enabled`
 * defers only the *call* to `queryFn`. A key that dereferences ctx therefore
 * throws on the very path ctx is allowed to be null on: a deployment with no
 * asset service, where the editor must still open.
 */
export function manifestQuery(ctx: AssetClientContext | null, assetId: string | null) {
    return {
        queryKey: assetKeys.manifest(ctx?.workspaceId ?? "", assetId ?? ""),
        // Safe: `enabled` keeps this from running until both are non-null.
        queryFn: () => deliveryManifest(ctx as AssetClientContext, assetId as string),
        staleTime: MANIFEST_STALE_TIME,
    };
}

/**
 * Resolves a thumbnail URL per asset, one manifest request each.
 *
 * `AssetRs` carries a `deliveryManifestUrl` — a pointer to a manifest — not an
 * image URL, so there is no way to render a tile without asking. The requests
 * run in parallel and are cached per asset for the session, and only `READY`
 * assets are asked about at all: the service returns no manifest URL for the
 * others, so asking would be a guaranteed miss.
 *
 * A failure yields a null thumbnail rather than a failed page. A missing asset
 * degrades, never fails.
 */
async function resolveThumbnails(
    ctx: AssetClientContext,
    queryClient: QueryClient,
    assets: readonly { id: string; state: string; type: AssetType }[],
): Promise<Map<string, string | null>> {
    const ready = assets.filter((asset) => asset.state === "READY");
    const entries = await Promise.all(
        ready.map(async (asset): Promise<[string, string | null]> => {
            const manifest = await withFallback<AssetDelivery | null>(null, () =>
                queryClient.fetchQuery(manifestQuery(ctx, asset.id)),
            );
            return [asset.id, manifest ? pickPreviewUrl(manifest, asset.type) : null];
        }),
    );
    return new Map(entries);
}

/**
 * The picker's `loadAssets`. Must not reject — see the contract on
 * `AssetPickerProps`; a rejection strands open UI with no visible change.
 */
export function buildAssetLoader(ctx: AssetClientContext, queryClient: QueryClient, type: AssetType): AssetLoader {
    return async (query: AssetQuery) => {
        const request = toSearchRequest(query, PAGE_SIZE, type);
        const page = await withFallback<AssetPage | null>(null, () =>
            queryClient.fetchQuery({
                queryKey: assetKeys.search(ctx.workspaceId, request),
                queryFn: () => searchAssets(ctx, request),
            }),
        );
        if (!page) return EMPTY_PAGE;
        const thumbnails = await resolveThumbnails(ctx, queryClient, page.items);
        return toSearchPage(page, thumbnails);
    };
}

/** The picker's `loadFolders`. Must not reject, same contract. */
export function buildFolderLoader(ctx: AssetClientContext, queryClient: QueryClient): FolderLoader {
    return async () => {
        const list = await withFallback<FolderList | null>(null, () =>
            queryClient.fetchQuery({
                queryKey: folderKeys.list(ctx.workspaceId),
                queryFn: () => listFolders(ctx),
            }),
        );
        return list ? list.items.map(toFolderSummary) : [];
    };
}

/**
 * The folder list as a hook, for surfaces that render folder names outside the
 * picker — the field's "in Brand" line, for one.
 */
export function useFolders(ctx: AssetClientContext | null): AssetFolderSummary[] {
    const { data } = useQuery({
        queryKey: folderKeys.list(ctx?.workspaceId ?? ""),
        queryFn: () => listFolders(ctx as AssetClientContext),
        enabled: ctx !== null,
    });
    return data ? data.items.map(toFolderSummary) : [];
}

/**
 * Resolves one artwork reference to a URL the preview can place.
 *
 * Shares the manifest query key with {@link useAssetById}, so the field's tile
 * and the preview's full-frame render cost one request between them and differ
 * only in which rendition they pick out of the same manifest.
 *
 * Null covers every not-yet and never: no asset service, no reference, the
 * manifest still in flight, or an asset with no `CONTAIN` rendition. The preview
 * renders without artwork in all of them, which is the same thing it does for a
 * template that has none — no error state, because none of these are the
 * operator's problem to solve.
 */
export function useArtworkUrl(ctx: AssetClientContext | null, assetId: string | null): string | null {
    const { data, isError } = useQuery({
        ...manifestQuery(ctx, assetId),
        enabled: ctx !== null && assetId !== null,
        retry: false,
    });
    // `isError`, not just `!data`: when the referenced asset is deleted its
    // manifest 404s on the next refetch, but React Query keeps the last good
    // `data` through the error — so without this guard the preview keeps
    // painting artwork whose asset is gone. Mirrors the same guard in
    // `useAssetById`; here it clears the ticket, there it clears the field.
    if (isError || !data) return null;
    return pickArtworkUrl(data);
}

/** The font file URL for an uploaded font, for the preview and the Your font
 *  card. Same errored-manifest guard as `useArtworkUrl`. */
export function useFontUrl(ctx: AssetClientContext | null, assetId: string | null): string | null {
    const { data, isError } = useQuery({
        ...manifestQuery(ctx, assetId),
        enabled: ctx !== null && assetId !== null,
        retry: false,
    });
    if (isError || !data) return null;
    return pickFontUrl(data);
}

/**
 * Resolves the asset a template's config references, with its thumbnail.
 *
 * `notFound` is a first-class outcome rather than an error: an id may point at
 * a deleted asset, because vouchers performs no cross-service referential
 * validation. The field renders a placeholder and says so; it must never clear
 * the form, which would discard the operator's data over a transient failure.
 */
export function useAssetById(ctx: AssetClientContext | null, assetId: string | null) {
    const detail = useQuery({
        queryKey: assetKeys.detail(ctx?.workspaceId ?? "", assetId ?? ""),
        queryFn: () => getAsset(ctx as AssetClientContext, assetId as string),
        enabled: ctx !== null && assetId !== null,
        retry: false,
    });

    const manifest = useQuery({
        ...manifestQuery(ctx, assetId),
        enabled: ctx !== null && assetId !== null && detail.data?.state === "READY",
        retry: false,
    });

    // React Query keeps the last successful `data` when a later refetch errors,
    // so a just-deleted asset would still resolve to its old summary here — the
    // field would show the stale thumbnail AND the "no longer in the library"
    // notice at once. Treat an errored detail as no asset, so the field falls
    // back to its empty state; the form still holds the id (see `notFound`).
    const asset =
        detail.data && !detail.isError
            ? toAssetSummary(detail.data, manifest.data ? pickPreviewUrl(manifest.data, detail.data.type) : null)
            : null;

    return {
        asset,
        isLoading: detail.isLoading,
        /** The id resolves to nothing. Show a placeholder, keep the value. */
        notFound: assetId !== null && detail.isError,
    };
}
